import { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Button, Typography } from '@material-ui/core';
import { withStyles } from '@material-ui/core/styles';
import styled from 'styled-components';
import Select from 'react-select';
import { modalsToggle, auctionVehiclesSetModal } from 'actions';
import ConfirmModal from 'components/Modals/ConfirmModal';
import { useGetUsersQuery } from '../../../../../../../services/api/vehicle-actions';
import { useViewedVehicle } from '../../../../hooks/useViewedVehicle';
import { User } from '../../../../../../../types/user';
import { AdminAwardBuyerModal } from '../../../../../AuctionDetail/components';
import { InfoBlock } from '../../../InfoBlock';
import { skipToken } from '@reduxjs/toolkit/dist/query';
import { useGetAuctionByIdQuery } from 'services/api/auctions-list';
import { useGetBuyerFeesQuery } from 'services/api/buyer-fees';

const modalTitle = 'confirmAwardModal';
const chargeModalTitle = 'auctionDetailAwardBuyerModal';

const StyledRoundedBlock = styled.div`
  border-radius: 4px;
  border-top-right-radius: 20px;
  border-bottom-right-radius: 20px;
  gap: 16px;

  background: #fff;
  width: 100%;
  display: flex;
  align-items: center;
`;

const selectStyles = {
  control: (styles: any) => ({
    ...styles,
    backgroundColor: 'none',
    border: 'none',
    fontSize: '16px'
  }),
  placeholder: (styles: any) => ({
    ...styles,
    color: '#B0B0B0',
    fontSize: '16px'
  })
};

const styles = {};

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  padding: 20px 16px;
  width: 100%;
  gap: 16px;
`;

const StyledButton = styled(Button)`
  border-radius: 20px;
  width: 100%;
`;

const ButtonWrapper = styled.div`
  padding: 3px 0;
  min-width: 180px;
  max-width: 180px;
  height: 44px;

  @media (max-width: 1024px) {
    min-width: 160px;
    max-width: 160px;
  }
`;

const StyledSelect = styled(Select)`
  flex: 1 1 100%;
`;

const Title = styled(Typography)`
  font-size: 18px;
  color: #212121;
  font-weight: 700;
  margin-bottom: 0;
`;

const AwardBuyer = () => {
  const { data: { users: allUsers = [] } = { users: [] } } = useGetUsersQuery();
  const vehicle = useViewedVehicle();
  const dispatch = useDispatch();
  const auctionVehicleModal = useSelector((state: any) => state.auctions.auctionVehiclesModal);
  const [buyerForAward, setBuyerForAward] = useState<User | null>(null);

  const { data: auctionData } = useGetAuctionByIdQuery(vehicle?.auction?.id || skipToken);
  const { data: fees = [] } = useGetBuyerFeesQuery(auctionData?.type, { skip: !auctionData });

  const users = useMemo(() => allUsers.filter(u => u.role === 'buyer'), [allUsers]);

  useEffect(() => {
    if (vehicle) {
      dispatch(auctionVehiclesSetModal(vehicle));
    }
  }, [vehicle]);

  if (!vehicle) return null;


  const handleChangeBuyer = (item: User) => {
    setBuyerForAward(item);
  };

  const renderDropDown = () => {
    const uniqueBidders = vehicle.bids.reduce((acc: any[], { bidder }: any) => {
      const isBidderAlreadyExists = acc.find(existingBidder => existingBidder.value === bidder.id);
      if (isBidderAlreadyExists) return acc;
      acc.push({
        value: bidder.id,
        label: `Bidder: ${bidder.dealership_name}`
      });
      return acc;
    }, [] as any[]);

    const usersWithoutBidders = (users || []).reduce<User[]>((acc, user) => {
      const isBidderAlreadyExists = uniqueBidders.find((existingBidder: any) => existingBidder.value === user.id);
      if (isBidderAlreadyExists) return acc;
      acc.push({
        value: user.id,
        label: user.dealership_name
      });
      return acc;
    }, []);

    return [...uniqueBidders, ...usersWithoutBidders];
  };

  const handleClickAward = () => {
    dispatch(modalsToggle(modalTitle));
  };

  const handleAward = () => {
    const preSelectedBuyer = buyerForAward;
    dispatch(auctionVehiclesSetModal({ ...vehicle, preSelectedBuyer, predefinedAwardPrice: null }));
    dispatch(modalsToggle(modalTitle));
    dispatch(modalsToggle(chargeModalTitle));
  };

  return (
    <InfoBlock>
      <Wrapper>
        <Title>Award Vehicle to Buyer</Title>
        <StyledRoundedBlock>
          <StyledSelect
            components={{ IndicatorSeparator: null }}
            options={renderDropDown()}
            value={buyerForAward}
            onChange={handleChangeBuyer}
            styles={selectStyles}
            placeholder="Enter / Select Buyer"
          />
          <ButtonWrapper>
            <StyledButton
              onClick={handleClickAward}
              color="primary"
              variant="contained"
              fullWidth
              disabled={!buyerForAward}
            >
              Award
            </StyledButton>
          </ButtonWrapper>
        </StyledRoundedBlock>
        {(vehicle.bids || vehicle.vehicle_price) && (
          <ConfirmModal
            submitText={'Submit'}
            modalId={modalTitle}
            title={`You are about to award ${(buyerForAward || {}).label} the vehicle ${vehicle.year} ${vehicle.make} ${
              vehicle.model
            }?`}
            handleSubmit={() => {
              handleAward();
            }}
          />
        )}

        {auctionVehicleModal && (
          <AdminAwardBuyerModal fees={fees} initialValues={{ vehicle_id: vehicle.id }} vehicle={vehicle} />
        )}
      </Wrapper>
    </InfoBlock>
  );
};

export default withStyles(styles)(AwardBuyer);
